/**
 * The payload hash — ported from the DI-T1 spike (`jpeg/hash.ts`).
 *
 * "Payload" is the compressed scan: every byte from the first SOS marker (the
 * 0xFF of 0xFFDA) to the end of the bytes given. Hashing that rather than the
 * whole file is the point — the metadata in front of it is rewritten by the
 * M400 gimbal in the field and by our own write-back (DI-5-1,
 * `writeBack/` — it re-checks this hash before = after on every file), and
 * the scan is not:
 * byte-identical on 918/918 files after T1's head rewrite, and MD5-identical
 * camera-SD vs gimbal-SD (`02` §6.1 finding 8). DI-D4 c1: this is the
 * duplicate key, computed on the ORIGINAL file (DI-D11).
 *
 * The same hash is computed independently by Python
 * (`_SPIKES/exif-xmp-write/py/jpegwalk.py`) in `__fixtures__/make-heads.mjs`,
 * so a bug here cannot certify itself.
 */

import { bufferSource, isJpeg, walkHead } from "./segments.js";

/** Offset of the first SOS in `bytes`, or null when there is none. */
export function scanStart(bytes: Uint8Array): number | null {
  if (!isJpeg(bytes)) return null;
  const head = walkHead(bytes);
  return head.sosOffset >= 0 ? head.sosOffset : null;
}

export function toHex(digest: ArrayBuffer): string {
  let s = "";
  for (const byte of new Uint8Array(digest))
    s += byte.toString(16).padStart(2, "0");
  return s;
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  return toHex(await crypto.subtle.digest("SHA-256", bufferSource(bytes)));
}

/** SHA-256 of the scan data, or null when the bytes contain no SOS. */
export async function payloadHash(bytes: Uint8Array): Promise<string | null> {
  const from = scanStart(bytes);
  if (from === null) return null;
  return sha256Hex(bytes.subarray(from));
}
